import React, { useState, useEffect } from 'react';
import { X, Fuel, Check, MapPin } from 'lucide-react';
import RollingOdometerInput from '../shared/RollingOdometerInput';

/**
 * FuelStopModal - Record a fuel stop (for non-FRHV vehicles)
 * This counts as a REST period entry point.
 * Records: Odometer, fuel amount (optional), location (optional)
 */
const FuelStopModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  vehicle,
  loading = false
}) => {
  const [reading, setReading] = useState(0);
  const [fuelAmount, setFuelAmount] = useState('');
  const [fuelCost, setFuelCost] = useState('');
  const [location, setLocation] = useState('');
  
  const lastOdometer = vehicle?.last_odometer || 0;
  
  useEffect(() => {
    if (isOpen && vehicle) {
      setReading(lastOdometer);
      setFuelAmount('');
      setFuelCost('');
      setLocation('');
    }
  }, [isOpen, vehicle, lastOdometer]);
  
  const handleConfirm = () => {
    onConfirm({
      vehicle_id: vehicle.vehicle_id,
      odometer_reading: reading,
      fuel_amount_litres: fuelAmount ? parseFloat(fuelAmount) : null,
      fuel_cost: fuelCost ? parseFloat(fuelCost) : null,
      location: location || null
    });
  };
  
  if (!isOpen) return null;
  
  const distance = reading - lastOdometer;
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <Fuel className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Fuel Stop</h3>
              <p className="text-sm text-slate-400">{vehicle?.rego}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition"
            data-testid="fuel-stop-modal-close"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Info box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-sm text-blue-200">
            Record your fuel stop. This can be used to start a rest break.
          </div>
          
          {/* Rolling odometer input */}
          <RollingOdometerInput
            value={reading}
            onChange={setReading}
            label="Current Odometer"
            digits={6}
            minValue={lastOdometer}
          />
          
          {/* Distance traveled */}
          {distance > 0 && (
            <div className="text-center text-sm text-slate-400">
              Distance since last reading: <span className="text-emerald-400 font-bold">{distance.toLocaleString()} km</span>
            </div>
          )}
          
          {/* Fuel details (optional) */}
          <div className="space-y-3 pt-2">
            <div className="text-sm font-medium text-slate-300">Fuel Details (Optional)</div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Litres</label>
                <input
                  type="number"
                  value={fuelAmount}
                  onChange={(e) => setFuelAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                  data-testid="fuel-stop-litres"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Cost ($)</label>
                <input
                  type="number"
                  value={fuelCost}
                  onChange={(e) => setFuelCost(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                  data-testid="fuel-stop-cost"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-slate-400 mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Shell Dubbo"
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                data-testid="fuel-stop-location"
              />
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex gap-3 sticky bottom-0 bg-slate-800">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition"
            data-testid="fuel-stop-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || reading < lastOdometer}
            className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="fuel-stop-modal-confirm"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5" />
                Record Fuel Stop
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FuelStopModal;
